import React, { useState } from 'react';
import { FiFile, FiDownload, FiUpload, FiCheck, FiX, FiSend, FiPrinter, FiFolder, FiMail } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { emailService } from '../../services/emailService';
import { toast } from 'react-hot-toast';

const DigitalDealJacketSimple = ({ dealData = {}, dealId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSending, setIsSending] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState(dealData?.customer?.email || '');

  const handleDownloadDocument = (doc) => {
    // Create a new window with the document content
    const documentWindow = window.open('', '_blank', 'width=800,height=600');
    if (documentWindow) {
      documentWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${doc.name}</title>
            <style>
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                padding: 40px; 
                max-width: 800px; 
                margin: 0 auto;
              }
              h1 { color: #1a73e8; border-bottom: 2px solid #1a73e8; padding-bottom: 10px; }
              .header { margin-bottom: 30px; }
              .content { line-height: 1.6; }
              .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
              @media print { 
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${doc.name}</h1>
              <p><strong>Deal #:</strong> ${dealData?.id || 'DL001'}</p>
              <p><strong>Customer:</strong> ${dealData?.customer?.name || 'Customer Name'}</p>
              <p><strong>Vehicle:</strong> ${dealData?.vehicle || 'Vehicle Information'}</p>
              <p><strong>Status:</strong> ${doc.status}</p>
            </div>
            
            <div class="content">
              <p>This document is currently being generated. The full ${doc.name} will be available once all information is complete.</p>
              
              <h2>Document Details</h2>
              <ul>
                <li>Document Type: ${doc.name}</li>
                <li>Generated: ${new Date().toLocaleDateString()}</li>
                <li>Status: ${doc.status === 'pending' ? 'Pending Generation' : doc.status}</li>
              </ul>
            </div>
            
            <div class="footer">
              <p>Paradise-Performance</p>
              <p>305 Co Rd 3470, Paradise, TX 76073 | (817) 382-2316</p>
            </div>
          </body>
        </html>
      `);
      documentWindow.document.close();

      // Trigger print dialog for download/save as PDF
      setTimeout(() => {
        documentWindow.print();
      }, 250);
    }
  };

  const handleDownloadAll = () => {
    // Create a comprehensive document with all forms
    const allDocuments = [
      { name: 'Form 130-U (Title Application)', status: 'pending' },
      { name: 'Bill of Sale', status: 'pending' },
      { name: 'Buyers Guide', status: 'pending' },
      { name: 'Odometer Disclosure', status: 'pending' },
      { name: 'Insurance Verification', status: 'pending' },
      { name: 'Driver License', status: 'pending' }
    ];

    const documentContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Deal Jacket - ${dealData?.id || 'DL001'}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              padding: 40px; 
              color: #333;
            }
            h1 { color: #1a73e8; }
            h2 { color: #555; page-break-before: auto; }
            .document { 
              page-break-after: always; 
              padding: 20px; 
              border: 1px solid #ddd; 
              margin-bottom: 30px;
              border-radius: 5px;
            }
            .header { 
              background: #f5f5f5; 
              padding: 20px; 
              margin-bottom: 30px;
              border-radius: 5px;
            }
            @media print { 
              .document { page-break-after: always; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Digital Deal Jacket</h1>
            <p><strong>Deal ID:</strong> ${dealData?.id || 'DL001'}</p>
            <p><strong>Customer:</strong> ${dealData?.customer?.name || 'Customer Name'}</p>
            <p><strong>Vehicle:</strong> ${dealData?.vehicle || 'Vehicle Info'}</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
          
          ${allDocuments.map(doc => `
            <div class="document">
              <h2>${doc.name}</h2>
              <p><strong>Status:</strong> ${doc.status === 'pending' ? 'Pending Generation' : doc.status}</p>
              <p>This document will be generated once all required information is complete.</p>
              <br>
              <p style="color: #666; font-style: italic;">
                Document placeholder - actual content will be populated from the contract system.
              </p>
            </div>
          `).join('')}
          
          <div style="text-align: center; color: #666; margin-top: 50px;">
            <p>Paradise-Performance</p>
            <p>305 Co Rd 3470, Paradise, TX 76073 | (817) 382-2316</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(documentContent);
      printWindow.document.close();

      // Trigger print dialog after content loads
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const handleSendDocuments = async () => {
    if (!emailAddress) {
      setEmailModal(true);
      return;
    }

    setIsSending(true);
    try {
      const dealInfo = {
        id: dealId,
        name: `${dealData?.customer?.first_name || ''} ${dealData?.customer?.last_name || ''}`.trim(),
        email: emailAddress,
        vehicleYear: dealData?.vehicles?.year,
        vehicleMake: dealData?.vehicles?.make,
        vehicleModel: dealData?.vehicles?.model,
        vehicleVin: dealData?.vehicles?.vin,
        salePrice: dealData?.sale_price || dealData?.vehicles?.price,
        documents: [
          'Bill of Sale',
          'Purchase Agreement',
          'Finance Contract',
          'Warranty Information',
          'Vehicle History Report'
        ]
      };

      const result = await emailService.sendDealDocuments(dealInfo, emailAddress);

      if (result.success) {
        toast.success(`Documents sent successfully to ${emailAddress}`);
        setEmailModal(false);
      } else {
        toast.error(result.error || 'Failed to send documents');
      }
    } catch (error) {
      console.error('Error sending documents:', error);
      toast.error('Failed to send documents. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg">
      {/* Header */}
      <div className="p-6 border-b">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Digital Deal Jacket</h2>
        <p className="text-gray-600">
          Deal #{dealData?.deal_number || dealId?.slice(0, 8)}
        </p>
        <p className="text-sm text-gray-500">
          Customer: {dealData?.customer?.first_name} {dealData?.customer?.last_name}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b px-6">
        <div className="flex space-x-6">
          {['overview', 'documents', 'upload'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 font-medium capitalize ${activeTab === tab
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Deal Overview</h3>

            {/* Deal Details */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Customer Information</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium">
                      {dealData?.customer?.first_name} {dealData?.customer?.last_name}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="font-medium">{dealData?.customer?.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>{' '}
                    <span className="font-medium">{dealData?.customer?.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-3">Vehicle Information</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Vehicle:</span>{' '}
                    <span className="font-medium">
                      {dealData?.vehicle?.year} {dealData?.vehicle?.make} {dealData?.vehicle?.model}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">VIN:</span>{' '}
                    <span className="font-medium">{dealData?.vehicle?.vin || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Price:</span>{' '}
                    <span className="font-medium">${dealData?.sale_price?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-3">Financial Summary</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Sale Price:</span>
                  <p className="font-semibold text-lg">${(dealData?.sale_price || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Down Payment:</span>
                  <p className="font-semibold text-lg">${(dealData?.down_payment || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Amount:</span>
                  <p className="font-semibold text-lg">${(dealData?.total_amount || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Required Documents</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Form 130-U (Title Application)', status: 'pending' },
                { name: 'Bill of Sale', status: 'pending' },
                { name: 'Buyers Guide', status: 'pending' },
                { name: 'Odometer Disclosure', status: 'pending' },
                { name: 'Insurance Verification', status: 'pending' },
                { name: 'Driver License', status: 'pending' }
              ].map((doc, index) => (
                <div key={index} className="border rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiFile} className="w-5 h-5 text-gray-500 mr-3" />
                    <div>
                      <p className="font-medium">{doc.name}</p>
                      <p className="text-xs text-gray-500">Status: {doc.status}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownloadDocument(doc)}
                    className="text-primary-600 hover:text-primary-700"
                    title={`Download ${doc.name}`}
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Document generation is being set up. You can use the contract system to generate legal documents.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Upload Documents</h3>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Drag and drop files here or click to browse</p>
              <input
                type="file"
                className="hidden"
                id="file-upload"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="file-upload"
                className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer"
              >
                Select Files
              </label>
            </div>

            <div className="mt-4 text-sm text-gray-500">
              Accepted formats: PDF, JPG, PNG (Max 10MB per file)
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-6 border-t bg-gray-50">
        <div className="flex gap-3">
          <button
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
            onClick={handleSendDocuments}
            disabled={isSending}
          >
            <SafeIcon icon={isSending ? FiMail : FiSend} className="w-4 h-4 mr-2" />
            {isSending ? 'Sending...' : 'Send Documents'}
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center"
            onClick={() => window.print()}
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center"
            onClick={handleDownloadAll}
          >
            <SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" />
            Download All
          </button>
        </div>
      </div>

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Enter Customer Email</h3>
            <input
              type="email"
              placeholder="customer@email.com"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEmailModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (emailAddress) {
                    setEmailModal(false);
                    handleSendDocuments();
                  }
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalDealJacketSimple;
